import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import Checkbox from "@saleor/components/Checkbox";
import ProductPrice from "@saleor/components/ProductPrice";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import { AVATAR_MARGIN } from "@saleor/components/TableCellAvatar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { ProductListColumns } from "@saleor/config";
import { maybe, renderCollection } from "@saleor/misc";
import {
  getAttributeIdFromColumnValue,
  isAttributeColumnValue
} from "@saleor/products/components/ProductListPage/utils";
import { GridAttributes_grid_edges_node } from "@saleor/products/types/GridAttributes";
import { ProductList_products_edges_node } from "@saleor/products/types/ProductList";
import { ProductListUrlSortField } from "@saleor/products/urls";
import { ListActions, ListProps, SortPage } from "@saleor/types";
import { SmopUserTypeCode } from "@saleor/types/globalTypes";
import TDisplayColumn, {
  DisplayColumnProps
} from "@saleor/utils/columns/DisplayColumn";
import { getArrowDirection } from "@saleor/utils/sort";
import classNames from "classnames";
import React from "react";
import { FormattedMessage } from "react-intl";

import ProductListTableHead from "./ProductListTableHead";

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      colName: {
        width: "auto"
      },
      colPrice: {
        width: 300
      },
      colPublished: {
        width: 200
      },
      colType: {
        width: 200
      }
    },
    colAttribute: {
      width: 150
    },
    colFill: {
      padding: 0,
      width: "100%"
    },
    colName: {
      "&$colNameFixed": {
        width: 250
      }
    },
    colNameFixed: {},
    colNameHeader: {
      marginLeft: AVATAR_MARGIN
    },
    colNameWrapper: {
      display: "block"
    },
    colPrice: {
      textAlign: "right"
    },
    colPublished: {},
    colType: {},
    link: {
      cursor: "pointer"
    },
    table: {
      tableLayout: "fixed"
    },
    tableContainer: {
      overflowX: "scroll"
    },
    textLeft: {
      textAlign: "left"
    },
    textRight: {
      textAlign: "right"
    }
  }),
  { name: "ProductList" }
);

const DisplayColumn = TDisplayColumn as React.FunctionComponent<
  DisplayColumnProps<ProductListColumns>
>;

interface ProductListProps
  extends ListProps<ProductListColumns>,
    ListActions,
    SortPage<ProductListUrlSortField> {
  activeAttributeSortId: string;
  gridAttributes: GridAttributes_grid_edges_node[];
  isAccountManager: boolean;
  isStoreAdmin: boolean;
  products: ProductList_products_edges_node[];
  loading: boolean;
  userView: string;
  totalCount: number;
  onNextPage: () => void;
  onPreviousPage: () => void;
  selectedProducts: any[];
  handleSelectProducts: (productIds: string[], isChecked: boolean) => void;
}

export const ProductList: React.FC<ProductListProps> = props => {
  const {
    settings,
    disabled,
    isAccountManager,
    isStoreAdmin,
    pageInfo,
    products,
    loading,
    sort,
    toolbar,
    totalCount,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    onRowClick,
    onSort,
    userView,
    selectedProducts,
    handleSelectProducts
  } = props;

  const classes = useStyles(props);

  const gridAttributesFromSettings = settings.columns.filter(
    isAttributeColumnValue
  );

  const isStoreAdminKAMSView =
    isAccountManager && userView === SmopUserTypeCode.STORE_ADMIN;

  const numberOfColumns =
    isStoreAdmin || isStoreAdminKAMSView
      ? 3 + settings.columns.length
      : 2 + settings.columns.length;

  const handleToggleAll = event => {
    const isChecked = event.target.checked;
    handleSelectProducts(
      products.map(product => product.id),
      isChecked
    );
  };
  const productIds = maybe(() => products.map(product => product.id), []);

  const selectedOnPage = selectedProducts.filter(productId =>
    productIds.includes(productId)
  );

  const someProductsIncluded =
    selectedOnPage.length > 0 && selectedOnPage.length < settings.rowNumber;
  const getProductPrice = product => {
    const priceRangeUndiscounted = product?.pricing?.priceRangeUndiscounted;

    if (!priceRangeUndiscounted) {
      return "-";
    }

    const { start, stop } = priceRangeUndiscounted;
    const {
      gross: { amount: startAmount }
    } = start;
    const {
      gross: { amount: stopAmount }
    } = stop;

    if (startAmount === stopAmount) {
      return (
        <ProductPrice
          money={{
            amount: startAmount,
            currency: start.gross.currency
          }}
        />
      );
    } else {
      return (
        <>
          <ProductPrice
            money={{
              amount: startAmount,
              currency: start.gross.currency
            }}
          />
          {" - "}
          <ProductPrice
            money={{
              amount: stopAmount,
              currency: stop.gross.currency
            }}
          />
        </>
      );
    }
  };

  return (
    <div className={classes.tableContainer}>
      <ResponsiveTable className={classes.table}>
        <colgroup>
          <col />
          <col className={classes.colName} />
          <DisplayColumn column="status" displayColumns={settings.columns}>
            <col className={classes.colType} />
          </DisplayColumn>
          {gridAttributesFromSettings.map(gridAttribute => (
            <col className={classes.colAttribute} key={gridAttribute} />
          ))}
          <DisplayColumn column="price" displayColumns={settings.columns}>
            <col className={classes.colPrice} />
          </DisplayColumn>
        </colgroup>
        <ProductListTableHead
          colSpan={numberOfColumns}
          selected={selectedOnPage.length}
          disabled={disabled}
          items={products}
          toggleAll={handleToggleAll}
          toolbar={toolbar}
          indeterminate={someProductsIncluded}
          totalSelected={selectedProducts.length}
        >
          <TableCellHeader
            arrowPosition="right"
            className={classNames(classes.colName, {
              [classes.colNameFixed]: settings.columns.length > 4
            })}
            direction={
              sort.sort === ProductListUrlSortField.name
                ? getArrowDirection(sort.asc)
                : undefined
            }
            onClick={() => onSort(ProductListUrlSortField.name)}
          >
            <span className={classes.colNameHeader}>
              <FormattedMessage defaultMessage="Name" description="product" />
            </span>
          </TableCellHeader>
          <DisplayColumn column="status" displayColumns={settings.columns}>
            <TableCellHeader
              className={classes.colType}
              direction={
                sort.sort === ProductListUrlSortField.status
                  ? getArrowDirection(sort.asc)
                  : undefined
              }
              onClick={() => onSort(ProductListUrlSortField.status)}
            >
              <FormattedMessage
                defaultMessage="Status"
                description="product status"
              />
            </TableCellHeader>
          </DisplayColumn>
          {(isStoreAdmin || isStoreAdminKAMSView) && (
            <TableCellHeader
              className={classes.colType}
              direction={
                sort.sort === ProductListUrlSortField.status
                  ? getArrowDirection(sort.asc)
                  : undefined
              }
              onClick={() => onSort(ProductListUrlSortField.status)}
            >
              <FormattedMessage
                defaultMessage="Visibility"
                description="product visibilty"
              />
            </TableCellHeader>
          )}
          <DisplayColumn column="price" displayColumns={settings.columns}>
            <TableCellHeader
              className={classes.colPrice}
              direction={
                sort.sort === ProductListUrlSortField.price
                  ? getArrowDirection(sort.asc)
                  : undefined
              }
              textAlign="right"
              onClick={() => onSort(ProductListUrlSortField.price)}
            >
              <FormattedMessage
                defaultMessage="Price"
                description="product price"
              />
            </TableCellHeader>
          </DisplayColumn>
        </ProductListTableHead>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={numberOfColumns}
              settings={settings}
              hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : false
              }
              onPreviousPage={onPreviousPage}
              totalCount={totalCount}
            />
          </TableRow>
        </TableFooter>
        <TableBody>
          {products &&
            renderCollection(
              products,
              (product, index) => {
                const isSelected =
                  selectedProducts && product
                    ? selectedProducts.includes(product.id)
                    : false;

                return (
                  <TableRow
                    selected={isSelected}
                    hover={!!product}
                    key={product ? product.id : "skeleton"}
                    onClick={product && onRowClick(product.id)}
                    className={classes.link}
                    data-test="id"
                    data-test-id={maybe(() => product.id)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        disabled={disabled}
                        disableClickPropagation
                        id={`checkboxRow${index}`}
                        onChange={event =>
                          handleSelectProducts(
                            [product.id],
                            event.target.checked
                          )
                        }
                      />
                    </TableCell>
                    <TableCell id={`nameRow${index}`}>
                      <span>{product && product.name}</span>
                    </TableCell>
                    <DisplayColumn
                      column="status"
                      displayColumns={settings.columns}
                    >
                      <TableCell
                        id={`statusRow${index}`}
                        className={classes.colType}
                        data-test="product-type"
                      >
                        {product && product.productStatus}
                      </TableCell>
                    </DisplayColumn>
                    {(isStoreAdmin || isStoreAdminKAMSView) && (
                      <TableCell
                        className={classes.colType}
                        data-test="product-type"
                        id={`visibilityRow${index}`}
                      >
                        {product &&
                        product.productVisibility &&
                        product.productVisibility.isVisible
                          ? "Published"
                          : "Unpublished"}
                      </TableCell>
                    )}
                    {gridAttributesFromSettings.map(gridAttribute => (
                      <TableCell
                        className={classes.colAttribute}
                        key={gridAttribute}
                        data-test="attribute"
                        data-test-attribute={getAttributeIdFromColumnValue(
                          gridAttribute
                        )}
                      >
                        {maybe<React.ReactNode>(() => {
                          const attribute = product.attributes.find(
                            attribute =>
                              attribute.attribute.id ===
                              getAttributeIdFromColumnValue(gridAttribute)
                          );
                          if (attribute) {
                            return attribute.values
                              .map(value => value.name)
                              .join(", ");
                          }
                          return "-";
                        }, <Skeleton />)}
                      </TableCell>
                    ))}
                    <DisplayColumn
                      column="price"
                      displayColumns={settings.columns}
                    >
                      <TableCell
                        className={classes.colPrice}
                        id={`priceRow${index}`}
                      >
                        {loading ? <Skeleton /> : getProductPrice(product)}
                      </TableCell>
                    </DisplayColumn>
                  </TableRow>
                );
              },
              () => (
                <TableRow>
                  <TableCell colSpan={numberOfColumns}>
                    <FormattedMessage defaultMessage="No products found" />
                  </TableCell>
                </TableRow>
              )
            )}
        </TableBody>
      </ResponsiveTable>
    </div>
  );
};
ProductList.displayName = "ProductList";
export default ProductList;
